import React, { useState } from 'react';
import { Upload, X, Image as ImageIcon, ExternalLink } from 'lucide-react';

const ImageUploader = ({ 
  label, 
  value, 
  onChange, 
  helpText = 'Upload an image or enter a URL',
  accept = 'image/*'
}) => {
  const [dragOver, setDragOver] = useState(false);
  const [isUrlMode, setIsUrlMode] = useState(!!value);

  const handleFileUpload = async (files) => {
    const file = files[0];
    if (file) {
      try {
        // Create an image element to process the image
        const reader = new FileReader();
        reader.onload = async (e) => {
          const img = new Image();
          img.onload = async () => {
            // Create a canvas to resize and compress the image
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            // Set maximum dimensions for uploaded images (optimised for web)
            const MAX_WIDTH = 1920;
            const MAX_HEIGHT = 1080;
            
            let { width, height } = img;
            
            // Calculate new dimensions while maintaining aspect ratio
            if (width > height) {
              if (width > MAX_WIDTH) {
                height = height * (MAX_WIDTH / width);
                width = MAX_WIDTH;
              }
            } else {
              if (height > MAX_HEIGHT) {
                width = width * (MAX_HEIGHT / height);
                height = MAX_HEIGHT;
              }
            }
            
            // Set canvas dimensions
            canvas.width = width;
            canvas.height = height;
            
            // Draw the image with high quality
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(img, 0, 0, width, height);
            
            // Try WebP conversion first (if supported)
            let compressedDataURL;
            
            try {
              // Attempt WebP conversion with high compression
              compressedDataURL = canvas.toDataURL('image/webp', 0.75);
              
              // Check if WebP was actually created (some older browsers return PNG)
              if (!compressedDataURL.startsWith('data:image/webp')) {
                throw new Error('WebP not supported');
              }
              
              console.log('✅ Image converted to WebP format');
            } catch (webpError) {
              // Fallback to JPEG with higher compression
              compressedDataURL = canvas.toDataURL('image/jpeg', 0.70);
              console.log('✅ Image converted to JPEG format (WebP not supported)');
            }
            
            // Calculate compression ratio
            const originalSize = file.size;
            const compressedSize = Math.round((compressedDataURL.length - 'data:image/webp;base64,'.length) * 0.75);
            const compressionRatio = Math.round((1 - compressedSize / originalSize) * 100);
            
            console.log(`🗜️ Image compressed: ${(originalSize / 1024).toFixed(1)}KB → ${(compressedSize / 1024).toFixed(1)}KB (${compressionRatio}% reduction)`);
            
            onChange(compressedDataURL);
          };
          img.src = e.target.result;
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Error processing image:', error);
        // Fallback to basic processing
        const reader = new FileReader();
        reader.onload = (e) => onChange(e.target.result);
        reader.readAsDataURL(file);
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileUpload(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <ImageIcon className="h-4 w-4 text-gray-500" />
        <label className="block text-sm font-bold text-gray-700 font-inter">
          {label}
        </label>
      </div>

      {/* Mode Toggle */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setIsUrlMode(false)}
          className={`px-3 py-1 text-xs rounded ${!isUrlMode 
            ? 'bg-orange-600 text-white' 
            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Upload File
        </button>
        <button
          type="button"
          onClick={() => setIsUrlMode(true)}
          className={`px-3 py-1 text-xs rounded ${isUrlMode 
            ? 'bg-orange-600 text-white' 
            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Use URL
        </button>
      </div>

      {isUrlMode ? (
        /* URL Input */
        <div className="space-y-2">
          <div className="flex gap-2">
            <input
              type="url"
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder="https://example.com/image.jpg"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
            />
            <button
              type="button"
              onClick={() => onChange('')}
              className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : (
        /* File Upload Area */
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            dragOver 
              ? 'border-orange-400 bg-orange-50' 
              : 'border-gray-300 hover:border-gray-400'
          }`}
        >
          <Upload className="mx-auto h-8 w-8 text-gray-400 mb-2" />
          <p className="text-sm text-gray-600 font-inter mb-2">
            Drag and drop an image here, or click to select
          </p>
          <p className="text-xs text-gray-500 font-inter mb-3">
            Images are automatically compressed and converted to WebP for optimal performance
          </p>
          <input
            type="file"
            accept={accept}
            onChange={(e) => handleFileUpload(e.target.files)}
            className="hidden"
            id={`file-upload-${label.replace(/\s+/g, '-').toLowerCase()}`}
          />
          <label
            htmlFor={`file-upload-${label.replace(/\s+/g, '-').toLowerCase()}`}
            className="inline-block bg-white border border-gray-300 rounded px-3 py-1 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
          >
            Choose File
          </label>
        </div>
      )}

      {/* Image Preview */}
      {value && (
        <div className="relative">
          <img
            src={value}
            alt={label}
            className="w-full max-w-xs h-24 object-cover rounded-lg border border-gray-200"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'block';
            }}
          />
          <div style={{ display: 'none' }} className="w-full max-w-xs h-24 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
            <p className="text-sm text-gray-500">Failed to load image</p>
          </div>
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 hover:bg-red-700"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      {helpText && (
        <div className="text-xs text-gray-600 font-inter space-y-1">
          <p>{helpText}</p>
          <p><strong>Image Processing:</strong></p>
          <ul className="list-disc list-inside space-y-0.5">
            <li>Automatically compressed for faster loading (TinyPNG-style)</li>
            <li>Converted to WebP format when supported</li>
            <li>Resized to max 1920x1080 pixels (maintains aspect ratio)</li>
            <li>Supported formats: JPG, PNG, GIF, WebP</li>
            <li>File size reduction: typically 40-70% smaller</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;